import { createMachine, assign, setup } from "xstate";
import { ExemptionQuestions } from "utils/types/exemption-questions";
import { commonGuards } from "./_commonGuards";
import moment from "moment";

export const illinoisExemptionQuestionFlowMachine = setup({
	types: {
		context: {} as ExemptionQuestions.FlowContext,
		events: {} as ExemptionQuestions.FlowEvent,
		input: {} as Partial<ExemptionQuestions.FlowContext>,
	},
	actions: {
		updateContext: assign(({ context, event }) => {
			if ("context" in event) {
				return {
					...context,
					...event.context,
				};
			}
			return context;
		}),
	},
	guards: {},
	delays: {},
}).createMachine({
	id: "ILLINOIS_EXEMPTION",
	context: ({ input }) => ({ ...input }) as ExemptionQuestions.FlowContext,
	initial: "IDLE",
	states: {
		IDLE: {},
		INIT: {
			always: [
				{
					target: "INTRO",
					guard: ({ context, event }) => {
						const url = new URL(window.location.href);

						return (
							context.primaryResidenceValue === undefined &&
							url.searchParams.get("q") !== "primaryResidence"
						);
					},
				},
				{
					target: "PRIMARY_RESIDENCE",
					guard: ({ context, event }) =>
						context.primaryResidenceValue === undefined,
				},
				{
					target: "CURRENTLY_RECEIVING",
					guard: ({ context, event }) => {
						return context.currentlyReceivingValue === undefined;
					},
				},
				{
					target: "DOB",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.dobValue === undefined,
				},
				{
					target: "PURCHASE_DATE",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.purchaseDateValue === undefined,
				},
				{
					target: "MOVE_IN_DATE",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.moveInDateValue === undefined,
				},
				{
					target: "RARE_SITUATIONS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						[
							context.hasDisability === true,
							context.isVeteran === true,
							context.specialFieldsValue?.is_active_duty_personnel ===
								undefined,
							context.noneOfTheAbove === undefined,
						].some(value => value === true),
				},
				{
					target: "MARITAL_STATUS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.maritalStatusValue === undefined,
				},
				{
					target: "OWNERS",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						context.ownersValue === undefined,
				},
				{
					target: "ADDITIONAL_OWNER",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						!commonGuards.isSoleOwner(context) &&
						[
							context.additionalOwnerFirstNameValue === undefined,
							context.additionalOwnerLastNameValue === undefined,
							context.additionalOwnerDobValue === undefined,
							context.additionalOwnerEmailValue === undefined,
							context.additionalOwnerPhoneValue === undefined,
						].some(value => value === true),
				},
				{
					target: "OWNERSHIP_PERCENT",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						!commonGuards.isSoleOwner(context) &&
						context.ownerOneValue === undefined &&
						context.ownerTwoValue === undefined,
				},
				{
					target: "DL_UPLOAD",
					guard: ({ context, event }) =>
						commonGuards.currentlyReceivingNoOrNotSure(context) &&
						!context.ownerHasUploadedDlFile,
				},
				{
					target: "AGREEMENT",
					guard: ({ context, event }) =>
						[
							context.primaryResidenceValue === true,
							context.currentlyReceivingValue === "no" ||
								context.currentlyReceivingValue === "notSure",
						].every(value => value === true),
				},
			],
		},
		INTRO: {
			entry: [
				assign({
					exemptionsProgress: context => 0,
				}),
			],
			on: {
				next: {
					target: "PRIMARY_RESIDENCE",
				},
			},
		},
		PRIMARY_RESIDENCE: {
			entry: [
				assign({
					exemptionsProgress: context => 0,
				}),
			],
			on: {
				back: {
					target: "INTRO",
					actions: assign({
						primaryResidenceValue: context => undefined,
					}),
				},
				next: [
					{
						target: "CURRENTLY_RECEIVING",
						guard: ({ context, event }) =>
							commonGuards.isPrimaryResidence(context),
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							commonGuards.isNotPrimaryResidence(context),
					},
				],
			},
		},
		NON_AGREEMENT_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
		},
		CURRENTLY_RECEIVING: {
			entry: [
				assign({
					exemptionsProgress: context => (1 / 14) * 100,
				}),
			],
			on: {
				back: {
					target: "PRIMARY_RESIDENCE",
					actions: assign({
						currentlyReceivingValue: context => undefined,
					}),
				},
				next: [
					{
						target: "DOB",
						guard: ({ context, event }) =>
							commonGuards.currentlyReceivingNoOrNotSure(context),
					},
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							!commonGuards.currentlyReceivingNoOrNotSure(context),
					},
				],
			},
		},
		DOB: {
			entry: [
				assign({
					exemptionsProgress: context => (1.5 / 14) * 100,
				}),
			],
			on: {
				back: {
					target: "CURRENTLY_RECEIVING",
					actions: assign({
						dobValue: context => undefined,
					}),
				},
				next: [
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							moment().diff(context.dobValue, "years") >= 65,
					},
					{
						target: "PURCHASE_DATE",
					},
				],
			},
		},
		PURCHASE_DATE: {
			entry: [
				assign({
					exemptionsProgress: context => (2 / 14) * 100,
				}),
			],
			on: {
				back: {
					target: "DOB",
					actions: assign({
						purchaseDateValue: context => undefined,
					}),
				},
				next: {
					target: "MOVE_IN_DATE",
				},
			},
		},
		MOVE_IN_DATE: {
			entry: [
				assign({
					exemptionsProgress: context => (3 / 14) * 100,
				}),
			],
			on: {
				back: {
					target: "PURCHASE_DATE",
					actions: assign({
						purchaseDateValue: context => undefined,
					}),
				},
				next: {
					target: "HOMESTEAD_QUALIFIED",
				},
			},
		},
		HOMESTEAD_QUALIFIED: {
			entry: [
				assign({
					exemptionsProgress: context => (4 / 14) * 100,
				}),
			],
			on: {
				back: {
					target: "MOVE_IN_DATE",
				},
				next: {
					target: "RARE_SITUATIONS",
				},
			},
		},
		RARE_SITUATIONS: {
			entry: [
				assign({
					exemptionsProgress: context => (5 / 14) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "HOMESTEAD_QUALIFIED",
						actions: assign({
							hasDisability: context => undefined,
							specialFieldsValue: ({ context, event }) => ({
								...context.specialFieldsValue,
								is_65_years_or_older: undefined,
								disabled_veteran: undefined,
								recently_returned_from_active_duty: undefined,
								was_resident_of_assisted_living_facility: undefined,
								is_active_duty_personnel: undefined,
							}),
						}),
					},
				],
				next: [
					{
						target: "NON_AGREEMENT_CONFIRMATION",
						guard: ({ context, event }) =>
							context.hasDisability === true || context.isVeteran === true,
					},
					{
						target: "MARITAL_STATUS",
					},
				],
			},
		},
		MARITAL_STATUS: {
			entry: [
				assign({
					exemptionsProgress: context => (6 / 14) * 100,
				}),
			],
			on: {
				back: {
					target: "OWNERS",
					actions: assign({
						maritalStatusValue: context => undefined,
						specialFieldsValue: ({ context, event }) => ({
							...context.specialFieldsValue,
							spouse_details: undefined,
						}),
					}),
				},
				next: {
					target: "OWNERS",
				},
			},
		},
		OWNERS: {
			entry: [
				assign({
					exemptionsProgress: context => (7 / 14) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "MARITAL_STATUS",
						actions: assign({
							ownersValue: context => undefined,
						}),
					},
				],
				next: [
					{
						target: "ADDITIONAL_OWNER",
						guard: ({ context, event }) =>
							["Own With Spouse", "Other Persons"].includes(
								context.ownersValue ?? ""
							) ||
							(context.maritalStatusValue === "Married" &&
								context.ownersValue === "Sole Owner"),
					},
					{
						target: "DL_UPLOAD",
					},
				],
			},
		},
		ADDITIONAL_OWNER: {
			entry: [
				assign({
					exemptionsProgress: context => (8 / 14) * 100,
				}),
			],
			on: {
				back: {
					target: "OWNERS",
					actions: assign({
						additionalOwnerFirstNameValue: context => undefined,
						additionalOwnerLastNameValue: context => undefined,
						additionalOwnerDobValue: context => undefined,
						owner2SsnTin: context => undefined,
					}),
				},
				next: [
					{
						target: "OWNERSHIP_PERCENT",
					},
				],
			},
		},
		OWNERSHIP_PERCENT: {
			entry: [
				assign({
					exemptionsProgress: context => (9 / 14) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "ADDITIONAL_OWNER",
						guard: ({ context, event }) =>
							context.maritalStatusValue !== "Married",
						actions: assign({
							ownerOneValue: context => undefined,
							ownerTwoValue: context => undefined,
						}),
					},
				],
				next: [
					{
						target: "DL_UPLOAD",
					},
				],
			},
		},
		DL_UPLOAD: {
			entry: [
				assign({
					exemptionsProgress: context => (10 / 14) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "OWNERS",
						guard: ({ context, event }) => commonGuards.isSoleOwner(context),
						actions: assign({
							ownerDlFilesToUpload: context => [],
							ownerDlExemptValue: context => undefined,
						}),
					},
					{
						target: "OWNERSHIP_PERCENT",
						actions: assign({
							ownerDlFilesToUpload: context => [],
							ownerDlExemptValue: context => undefined,
						}),
					},
				],
				next: [
					{
						target: "IL_ON_HOLD_DISCLAIMER",
						guard: () => ![1, 2, 3].includes(moment().month()),
					},
					{
						target: "AGREEMENT",
					},
				],
			},
		},
		IL_ON_HOLD_DISCLAIMER: {
			entry: [
				assign({
					exemptionsProgress: context => (12 / 14) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "DL_UPLOAD",
					},
				],
				next: {
					target: "AGREEMENT",
				},
			},
		},
		AGREEMENT: {
			entry: [
				assign({
					exemptionsProgress: context => (13 / 14) * 100,
				}),
			],
			on: {
				back: [
					{
						target: "IL_ON_HOLD_DISCLAIMER",
						guard: () => ![1, 2, 3].includes(moment().month()),
					},
					{
						target: "DL_UPLOAD",
					},
				],
				next: {
					target: "MAIN_CONFIRMATION",
				},
			},
		},
		MAIN_CONFIRMATION: {
			entry: [
				assign({
					exemptionsProgress: context => 100,
				}),
			],
			on: {
				next: {
					target: "MAIN_CONFIRMATION",
				},
			},
		},
	},
	on: {
		update_context: {
			actions: "updateContext",
		},
		restart: {
			target: ".INIT",
		},
	},
});
